import React, { useCallback, useMemo, useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import {
  Icon,
  Blip,
  Box,
  Clickable,
  Loading,
  SearchInputGroup,
  MenuBox,
  MenuStateProvider,
  MenuItem,
  Menu,
  MenuGroup,
  MenuButton,
  useMenuStateContext,
  createToast
} from '@maui/elements'
import {
  useListThreatProfilesQuery,
  useSelectedThreatProfileQuery,
  useSelectedThreatProfileStorage
} from '@nautilusjs/mati-apis'

const useThreatProfilesData = () => {
  const normalizeThreatProfilesForDropdown = (data) => {
    const profiles = data?.profiles || []
    return (
      profiles.map((profile) => {
        return {
          label: profile.displayName,
          value: profile.name
        }
      }).sort((a, b) => a.label.localeCompare(b.label))
    )
  }
  const { data, isLoading } = useListThreatProfilesQuery({ select: normalizeThreatProfilesForDropdown })

  return {
    data,
    isLoading
  }
}

const useMenuWidthOptions = () => {
  const { visible: menuVisible } = useMenuStateContext()
  const initialWidth = '88px'
  const expandedWidth = '164px'
  const [width, setWidth] = useState(initialWidth)
  const handleMouseEnter = () => {
    setWidth(expandedWidth)
  }

  const handleMouseLeave = () => {
    if (menuVisible) return
    setWidth(initialWidth)
  }

  useEffect(() => {
    if (menuVisible) {
      setWidth(expandedWidth)
    }
    if (!menuVisible) {
      setWidth(initialWidth)
    }
  }, [initialWidth, menuVisible, expandedWidth])

  return {
    width,
    onMouseEnter: handleMouseEnter,
    onMouseLeave: handleMouseLeave,
  }
}

export const useThreatProfilesOptions = () => {
  const { data, isLoading } = useThreatProfilesData()

  const { data: currentProfile } = useSelectedThreatProfileQuery()
  const [, setProfileId] = useSelectedThreatProfileStorage()

  const { name, displayName } = currentProfile || {}
  const [inputValue, setInputValue] = useState('')

  const onItemChange = useCallback((profile) => {
    setProfileId(profile.value)
    createToast({
      title: 'Profile selected',
      message: `Selected profile: "${profile.label}"`,
      variant: 'success',
      duration: 5000
    })
  }, [setProfileId])

  const filteredThreatProfiles = useMemo(() => {
    return data?.filter(item => item.label.toLowerCase().includes(inputValue.toLowerCase()))
  }, [inputValue, data])

  return {
    isLoading,
    threatProfiles: data,
    filteredThreatProfiles,
    selectedItemId: name,
    selectedItem: displayName,
    setItem: onItemChange,
    inputValue,
    setInputValue,
  }
}

const ThreatProfilesMenuItems = ({ isLoading, threatProfiles, setItem, selectedItemId }) => {
  if (isLoading) {
    return (
      <Box
        width="235px"
      >
        <Loading />
        <Box textStyle="caption">
          Loading your threat profiles...
        </Box>
      </Box>
    )
  }

  if (!threatProfiles.length) {
    return (
      <Box
        color="colorTextMain"
        textStyle="bodySmall"
        width="235px"
        p="spacePaddingLarge"
      >
        No results found
      </Box>
    )
  }

  const currentLocation = window.location.pathname
  const isInTpApp = currentLocation.includes('/threat-profile/create') || currentLocation.includes('/threat-profiles')

  return threatProfiles.map(({ label, value }) =>
    (
      <MenuItem
        height="40px"
        key={value}
        backgroundColor={value === selectedItemId ? 'colorFillMain' : 'transparent'}
        onClick={() => {
          setItem({ label, value })
          if (isInTpApp) {
            window.history.pushState(null, null, '/threat-profile')
          }
        }}
        borderLeftColor={value === selectedItemId ? 'colorActionSecondaryDefault' : 'transparent'}
        borderLeftStyle="solid"
        borderLeftWidth="1px"
      >
        <Box
          color="colorTextMain"
          textStyle="bodySmall"
          width="235px"
          textOverflow="ellipsis"
          overflow="hidden"
          whiteSpace="nowrap"
          title={label}
        >
          {label}
        </Box>
      </MenuItem>
    ))
}

ThreatProfilesMenuItems.propTypes = {
  isLoading: PropTypes.bool,
  threatProfiles: PropTypes.array,
  handleToggle: PropTypes.func,
  setItem: PropTypes.func,
  selectedItemId: PropTypes.string
}

export const ThreatProfiles = ({
  isLoading,
  selectedItemId,
  selectedItem,
  setItem,
  threatProfiles,
  filteredThreatProfiles,
  inputValue,
  setInputValue,
}) => {
  const { width, onMouseEnter, onMouseLeave } = useMenuWidthOptions()
  const { toggle } = useMenuStateContext()

  if (isLoading || !threatProfiles?.length) {
    return null
  }

  const createRoute = '/threat-profile/create'

  return (
    <Box onMouseEnter={onMouseEnter} onMouseLeave={onMouseLeave}>
      <MenuButton variant="text" color="colorTextMain" onClick={() => localStorage.removeItem('recentlyCreatedThreatProfile')}>
        <Box
          display="flex"
          alignItems='center'
          gap="spaceGapSmall"
          overflow="hidden"
          textOverflow="ellipsis"
          whiteSpace="nowrap"
          width={width}
          transition="width 0.2s linear"
          textTransform="initial"
        >
          { localStorage.getItem('recentlyCreatedThreatProfile') === selectedItemId && <Blip /> }{selectedItem}
        </Box>
        <Icon name='chevronDown' />
      </MenuButton>
      <Menu aria-label='Threat Profiles Menu' width="235px">
        <MenuGroup
          label='Threat Profiles'
          slotRight={
            <Box display="flex" flexDirection="row" gap="spaceGapMedium">
              <Clickable focusable as="a" href={createRoute} title="Create Threat Profile" aria-label="Create Threat Profile" onClick={toggle}>
                <Icon color="colorActivePrimary" name="add" />
              </Clickable>
              <Box
                backgroundColor="colorBorderMain"
                width="1px"
                height="auto"
              />
              <Clickable focusable as="a" href="/threat-profiles" title="Manage Threat Profiles" aria-label="Manage Threat Profiles" onClick={toggle}>
                <Icon color="colorActivePrimary" name="cog" />
              </Clickable>
            </Box>
          }
        >
          <MenuBox>
            <SearchInputGroup
              flex="1"
              mb="8px"
              inputProps={{
                onChange: (e) => setInputValue(e.target.value),
                value: inputValue,
                placeholder: 'Filter by name'
              }}
            />
          </MenuBox>
          <Box
            maxHeight='calc(100vh - 160px)'
            overflowY='auto'
          >
            <ThreatProfilesMenuItems
              isLoading={isLoading}
              threatProfiles={filteredThreatProfiles}
              setItem={setItem}
              selectedItem={selectedItem}
              selectedItemId={selectedItemId}
            />
          </Box>
        </MenuGroup>
      </Menu>
    </Box>
  )
}

ThreatProfiles.propTypes = {
  selectedItem: PropTypes.string,
  selectedItemId: PropTypes.string,
  setItem: PropTypes.func,
  threatProfiles: PropTypes.array,
  filteredThreatProfiles: PropTypes.array,
  isLoading: PropTypes.bool,
  inputValue: PropTypes.string,
  setInputValue: PropTypes.func,
}

export const ThreatProfilesMenu = (props) => (
  <MenuStateProvider>
    <ThreatProfiles {...useThreatProfilesOptions()} {...props}/>
  </MenuStateProvider>
)
